import React from 'react';

/**
 * One action in a TanTable's expandable "action bar" — the inline toolbar row
 * toggled open by the action-bar icon. Actions run against the table's
 * CURRENTLY-FILTERED rows (column filters + search), NOT the whole dataset, so
 * a mass action on a user-built dashboard tile only touches that tile's visible
 * PROs. This replaces the page-level "Change All / Change Template for All /
 * Trace All" header buttons, whose scope was ambiguous once a workspace holds
 * several tables at once.
 */
export interface BulkAction {
  /** Stable key (used as React key). */
  key: string;
  label?: string;
  icon?: React.ReactNode;
  /** Button appearance override (Tailwind). Defaults to a neutral pill. */
  className?: string;
  disabled?: boolean;
  title?: string;
  /** Simple action: invoked with the filtered row originals, resolved at click
   *  time so the scope is always the table's current visible rows. */
  onRun?: (rows: any[]) => void;
  /** Escape hatch for actions that render their own control (a submenu/portal),
   *  e.g. Trace All with its SSLine/Terminal dropdown. Given a lazy getter for
   *  the filtered row originals. When set, label/icon/onRun are ignored. */
  render?: (getFilteredRows: () => any[]) => React.ReactNode;
  /** Hide without removing — keeps a stable action list across renders. */
  hidden?: boolean;
}

/** The action-bar toggle glyph (a header bar with a dropdown chevron over list
 *  rows). Opens/closes the inline action bar in a TanTable toolbar. Fill uses
 *  currentColor so it inherits the toolbar's text color. */
/* viewBox padded so the glyph fills ~72% of the box — the band the heroicons beside
   it in the TanTable toolbar occupy. The source art drew edge-to-edge (ink 0..22 of
   24), which rendered it noticeably taller than its neighbours. */
export const ActionBarToggleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="-3.28 -4.28 30.56 30.56" fill="currentColor" className={className} aria-hidden="true">
    <path d="M22,14c0,.552-.447,1-1,1H9c-.552,0-1-.448-1-1s.448-1,1-1h12c.553,0,1,.448,1,1ZM5,12H3c-.552,0-1,.448-1,1v2c0,.552,.448,1,1,1h2c.552,0,1-.448,1-1v-2c0-.552-.448-1-1-1Zm16,7H9c-.552,0-1,.448-1,1s.448,1,1,1h12c.553,0,1-.448,1-1s-.447-1-1-1Zm-16-1H3c-.552,0-1,.448-1,1v2c0,.552,.448,1,1,1h2c.552,0,1-.448,1-1v-2c0-.552-.448-1-1-1ZM24,4v2c0,2.206-1.794,4-4,4H4C1.794,10,0,8.206,0,6v-2C0,1.794,1.794,0,4,0H20c2.206,0,4,1.794,4,4Zm-2,0c0-1.103-.897-2-2-2H4c-1.103,0-2,.897-2,2v2c0,1.103,.897,2,2,2H20c1.103,0,2-.897,2-2v-2Zm-2.886-.5h-4.257c-.697,0-1.043,.846-.546,1.334l1.858,1.825c.455,.455,1.177,.455,1.632,0l1.858-1.825c.498-.489,.152-1.334-.546-1.334Z" />
  </svg>
);

const BTN_CLS =
  'inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded border transition-colors ' +
  'text-slate-700 dark:text-gray-100 bg-panel border-slate-300 dark:border-gray-600' +
  'hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed';

interface TableActionBarProps {
  actions: BulkAction[];
  /** Lazy getter for the table's filtered row originals (evaluated per click). */
  getFilteredRows: () => any[];
  /** Count of rows currently in view, shown as a scope reminder. */
  visibleCount: number;
}

/**
 * The inline action-bar row rendered under a TanTable toolbar when opened. A
 * leading "N in view" chip reminds the user the scope is this table's visible
 * rows; each action runs against those filtered rows.
 */
const TableActionBar: React.FC<TableActionBarProps> = ({ actions, getFilteredRows, visibleCount }) => {
  const shown = actions.filter((a) => !a.hidden);
  if (shown.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap px-3 py-2 bg-[rgb(229,231,235)] dark:bg-slate-700 border-y border-slate-300 dark:border-slate-600">
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 whitespace-nowrap">
        {visibleCount} in view
      </span>
      <span className="h-4 w-px bg-slate-300 dark:bg-slate-500" aria-hidden />
      {shown.map((a) =>
        a.render ? (
          <React.Fragment key={a.key}>{a.render(getFilteredRows)}</React.Fragment>
        ) : (
          <button
            key={a.key}
            type="button"
            disabled={a.disabled}
            title={a.title}
            onClick={() => a.onRun?.(getFilteredRows())}
            className={a.className ?? BTN_CLS}
          >
            {a.icon}
            {a.label}
          </button>
        ),
      )}
    </div>
  );
};

export default TableActionBar;
